import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import HeroImageUpload from "@/components/HeroImageUpload";
import {
  ArrowLeft, Loader2, Mail, Phone, ShieldCheck, User as UserIcon, Briefcase,
  GraduationCap, Award, HeartPulse, CalendarOff, StickyNote, Plane, MapPin, Clock,
} from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };
const expiryTone = (days) => days == null ? "text-slate-400" : days < 0 ? "text-red-600" : days <= 30 ? "text-amber-600" : "text-emerald-600";
const expiryLabel = (days) => days == null ? "" : days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? "Expires today" : `${days}d left`;

const Section = ({ icon: Icon, title, count, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
    <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4 text-lime-500" /> {title}{count != null && <span className="text-slate-400 font-medium normal-case tracking-normal">· {count}</span>}
    </h2>
    {children}
  </div>
);

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get(`/users/${id}/profile`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">User not found. <button onClick={() => navigate("/users")} className="text-lime-600 underline">Back to users</button></div>;

  const { user, employee, certifications, jobs, availability, training, notes } = data;
  const heroPhoto = user.photo || null;
  const JobRow = ({ j }) => (
    <button onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`user-job-${j.id}`}
      className="w-full text-left flex items-center gap-3 py-2 border-t border-slate-100 first:border-0 hover:bg-slate-50 rounded px-2 -mx-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{j.title}</p>
        <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(j.start)}</span>
          {j.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>}
        </p>
      </div>
      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{j.status}</span>
    </button>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="user-detail-page">
      <button onClick={() => navigate("/users")} data-testid="user-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to users
      </button>

      {/* Header with hero photo */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-56 h-48 sm:h-auto bg-slate-100 shrink-0">
            <HeroImageUpload testid="user-hero" label="Add photo" rounded="rounded-none" className="h-full w-full min-h-[192px]"
              imagePath={heroPhoto}
              onUploaded={async (path) => { await api.put(`/users/${user.user_id}/photo`, { photo: path }); load(); }} />
          </div>
          <div className="p-6 flex-1 min-w-0">
            <h1 className="text-2xl font-bold font-heading text-slate-900">{user.name}</h1>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" />{user.email}</span>
              {user.mobile && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4 text-slate-400" />{user.mobile}</span>}
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-slate-400" />{user.role}</span>
              {employee && <span className="inline-flex items-center gap-1.5"><UserIcon className="h-4 w-4" style={{ color: employee.color }} />{employee.role || "Crew"}</span>}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Stat label="Jobs (PIC)" value={jobs.as_pic} />
              <Stat label="Jobs (crew)" value={jobs.as_crew} />
              {employee?.flight_hours != null && <Stat label="Flight hours" value={employee.flight_hours} />}
              <Stat label="Notes" value={notes.created} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Licences & medicals */}
        <Section icon={Award} title="Licences & Medicals" count={certifications.length} testid="user-certifications">
          {certifications.length === 0 ? <p className="text-sm text-slate-400">No certifications on record.</p> : (
            <div className="space-y-1">
              {certifications.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-t border-slate-100 first:border-0" data-testid={`user-cert-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                    {c.number && <p className="text-xs text-slate-400">#{c.number}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-500">{c.expiry ? fmtDate(c.expiry) : "No expiry"}</p>
                    {c.expiry && <p className={`text-xs font-medium ${expiryTone(c.days_until)}`}>{expiryLabel(c.days_until)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Training */}
        <Section icon={GraduationCap} title="Training" testid="user-training">
          {!training ? <p className="text-sm text-slate-400">No training record linked to this user.</p> : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                <Stat label="Completion" value={`${training.summary.percent}%`} />
                <Stat label="Lessons" value={`${training.summary.competent}/${training.summary.total_lessons}`} />
                <Stat label="Hours" value={training.summary.hours_logged} />
              </div>
              {training.medical_class && (
                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                  <span className="text-sm text-slate-600 inline-flex items-center gap-1.5"><HeartPulse className="h-4 w-4 text-rose-500" /> Medical · {training.medical_class}</span>
                  <span className={`text-xs font-medium ${expiryTone(training.medical_days_until)}`}>{training.medical_expiry ? `${fmtDate(training.medical_expiry)} · ${expiryLabel(training.medical_days_until)}` : "—"}</span>
                </div>
              )}
              {training.programs.map((p) => (
                <div key={p.id} className="border-t border-slate-100 pt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-800">{p.name}</span>
                    <span className="text-slate-500">{p.percent}%</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-lime-400" style={{ width: `${p.percent}%` }} /></div>
                </div>
              ))}
              {training.reference_no && <p className="text-xs text-slate-400">Reference: {training.reference_no}</p>}
            </div>
          )}
        </Section>

        {/* Upcoming jobs */}
        <Section icon={Briefcase} title="Upcoming Jobs" count={jobs.upcoming.length} testid="user-upcoming-jobs">
          {jobs.upcoming.length === 0 ? <p className="text-sm text-slate-400">No upcoming jobs.</p> : jobs.upcoming.map((j) => <JobRow key={j.id} j={j} />)}
        </Section>

        {/* Availability */}
        <Section icon={CalendarOff} title="Availability" count={availability.length} testid="user-availability">
          {availability.length === 0 ? <p className="text-sm text-slate-400">No unavailability recorded.</p> : (
            <div className="space-y-1">
              {availability.map((a) => (
                <div key={a.id} className="flex items-center gap-2 py-2 border-t border-slate-100 first:border-0">
                  <span className={`h-2.5 w-2.5 rounded-full ${a.type === "dnc" ? "bg-red-500" : "bg-amber-500"}`} />
                  <span className="text-sm text-slate-700">{fmtDate(a.start)}{a.end && a.end !== a.start ? ` – ${fmtDate(a.end)}` : ""}</span>
                  <span className={`text-xs ${a.type === "dnc" ? "text-red-600" : "text-amber-600"}`}>{a.type === "dnc" ? "Do not contact" : "OK to contact"}</span>
                  {a.reason && <span className="text-xs text-slate-400 truncate">· {a.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Recent jobs */}
        {jobs.recent.length > 0 && (
          <Section icon={Plane} title="Recent Jobs" count={jobs.recent.length} testid="user-recent-jobs">
            {jobs.recent.map((j) => <JobRow key={j.id} j={j} />)}
          </Section>
        )}

        {/* Notes summary */}
        <Section icon={StickyNote} title="Notes" testid="user-notes">
          <div className="flex flex-wrap gap-3">
            <Stat label="Created" value={notes.created} />
            <Stat label="Assigned to" value={notes.assigned} />
          </div>
        </Section>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-w-[84px]">
    <p className="text-lg font-bold text-slate-900 font-heading leading-none">{value}</p>
    <p className="text-[11px] text-slate-500 mt-1">{label}</p>
  </div>
);
